import { api, WorkspacesAPITagType } from '@/api/api'
import getNamedInfiniteQueryConfig from '@/api/utils/get-named-infinite-query-config'
import getWorkspaceApiUrl from '@/api/utils/get-workspace-api-url'
import {
  customQuotationListSchema,
  fetchCustomQuotationsResponseSchema,
  fetchPredictiveReportQuotationsResponseSchema,
  predictiveReportQuotationListSchema,
  type AddQuotationRequest,
  type DeleteQuotationRequest,
  type EditQuotationRequest,
  type FetchCustomQuotationsResponse,
  type FetchPredictiveReportQuotationsResponse,
  type FetchQuotationsRequest,
} from '@/types/schema/directory/quotation'

export const quotationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    addQuotation: builder.mutation<void, AddQuotationRequest>({
      query: ({ kolId, quotation }) => ({
        url: getWorkspaceApiUrl(`/kols/${kolId}/quotation`, 'v3'),
        method: 'POST',
        body: quotation,
      }),
      invalidatesTags: [
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.DirectoryKolFootprint,
      ],
    }),
    deleteQuotation: builder.mutation<void, DeleteQuotationRequest>({
      query: ({ kolId, quotationId }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${kolId}/quotation/${quotationId}`,
          'v3',
        ),
        method: 'DELETE',
      }),
      invalidatesTags: [
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.DirectoryKolFootprint,
      ],
    }),
    editQuotation: builder.mutation<void, EditQuotationRequest>({
      query: ({ kolId, quotationId, quotation }) => ({
        url: getWorkspaceApiUrl(
          `/kols/${kolId}/quotation/${quotationId}`,
          'v3',
        ),
        method: 'PUT',
        body: quotation,
      }),
      invalidatesTags: [
        WorkspacesAPITagType.DirectoryQuotation,
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.DirectoryKolFootprint,
      ],
    }),
    fetchCustomQuotationsInfinite: builder.query<
      FetchCustomQuotationsResponse,
      FetchQuotationsRequest
    >({
      query: ({ kolId, ...params }) => ({
        url: getWorkspaceApiUrl(`/kols/${kolId}/quotation/list`, 'v3'),
        params,
      }),
      ...getNamedInfiniteQueryConfig<
        FetchQuotationsRequest,
        FetchCustomQuotationsResponse,
        'quotation_list'
      >(customQuotationListSchema.keyof().enum.quotation_list),
      extraOptions: {
        dataSchema: fetchCustomQuotationsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.DirectoryQuotation],
    }),
    fetchPredictiveReportQuotationsInfinite: builder.query<
      FetchPredictiveReportQuotationsResponse,
      FetchQuotationsRequest
    >({
      query: ({ kolId, ...params }) => ({
        url: getWorkspaceApiUrl(`/kols/${kolId}/predictive-report/list`, 'v3'),
        params,
      }),
      ...getNamedInfiniteQueryConfig<
        FetchQuotationsRequest,
        FetchPredictiveReportQuotationsResponse,
        'predictive_report_quotation_list'
      >(
        predictiveReportQuotationListSchema.keyof().enum
          .predictive_report_quotation_list,
      ),
      extraOptions: {
        dataSchema: fetchPredictiveReportQuotationsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.PredictiveReportQuotation],
    }),
  }),
  overrideExisting: false,
})

export const useAddQuotationMutation =
  quotationApi.endpoints.addQuotation.useMutation
export const useDeleteQuotationMutation =
  quotationApi.endpoints.deleteQuotation.useMutation
export const useEditQuotationMutation =
  quotationApi.endpoints.editQuotation.useMutation
export const useFetchCustomQuotationsInfiniteQuery =
  quotationApi.endpoints.fetchCustomQuotationsInfinite.useQuery
export const useFetchPredictiveReportQuotationsInfiniteQuery =
  quotationApi.endpoints.fetchPredictiveReportQuotationsInfinite.useQuery
